<script setup lang="ts">
import { onBeforeUnmount, watch } from "vue";

import { Placeholder } from "@tiptap/extensions";
import StarterKit from "@tiptap/starter-kit";
import { EditorContent, useEditor } from "@tiptap/vue-3";

const props = withDefaults(
    defineProps<{
        modelValue?: string;
        placeholder?: string;
        autofocus?: boolean;
        disabled?: boolean;
    }>(),
    {
        modelValue: "",
        placeholder: "Сообщение...",
        autofocus: false,
        disabled: false,
    }
);

const emit = defineEmits<{
    "update:modelValue": [value: string];
    input: [];
    submit: [];
}>();

let skipUpdate = false;

const editor = useEditor({
    content: props.modelValue,
    editable: !props.disabled,
    extensions: [
        StarterKit,
        Placeholder.configure({ placeholder: props.placeholder }),
    ],
    autofocus: props.autofocus,
    onUpdate({ editor, transaction }) {
        emit("update:modelValue", editor.getHTML());
        if (!skipUpdate && transaction.docChanged) {
            emit("input");
        }
    },
    editorProps: {
        handleKeyDown(_, event) {
            if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                emit("submit");
                return true;
            }
            return false;
        },
    },
});

// Обновляем содержимое когда родитель меняет modelValue извне (режим редактирования).
function setContent(html: string) {
    if (!editor.value) {
        return;
    }
    if (editor.value.getHTML() !== html) {
        editor.value.commands.setContent(html);
    }
}

function clear() {
    skipUpdate = true;
    editor.value?.commands.clearContent(true);
    skipUpdate = false;
}

function focus() {
    editor.value?.commands.focus();
}

// Вставка текста (эмодзи из пикера) в текущую позицию курсора.
function insertText(text: string) {
    editor.value?.chain().focus().insertContent(text).run();
}

watch(
    () => props.disabled,
    (val) => editor.value?.setEditable(!val)
);

onBeforeUnmount(() => editor.value?.destroy());

defineExpose({ setContent, clear, focus, insertText });
</script>

<template>
    <div class="mc-editor">
        <EditorContent class="mc-editor__content" :editor="editor" />
    </div>
</template>

<style lang="scss">
.mc-editor {
    position: relative;
    flex: 1;
    display: flex;
    align-items: center;

    &__content {
        flex: 1;
        .tiptap {
            outline: none;
            font-size: 1em;
            font-weight: 550;
            line-height: 1.5;
            min-height: 30px;
            padding: 7px 0;
            padding-left: 5px;
            box-sizing: border-box;
            color: var(--mc-fg);

            p {
                margin: 0;
            }
            p + p {
                margin-top: 4px;
            }
            code {
                background: var(--mc-bg-rail);
                border: 1px solid var(--mc-line);
                padding: 1px 4px;
                font-size: 0.85em;
            }

            p.is-editor-empty:first-child::before {
                content: attr(data-placeholder);
                color: var(--mc-fg-dim);
                pointer-events: none;
                float: left;
                height: 0;
            }
        }
    }

    &__bubble {
        display: flex;
        gap: 2px;
        background: var(--mc-bg-window);
        border: 1px solid var(--mc-line-hard);
        padding: 3px;
    }

    &__bubble-btn {
        padding: 3px 8px;
        font-size: 13px;
        color: var(--mc-fg-mute);
        transition: 0.1s;
        min-width: 28px;

        &:hover {
            color: var(--mc-fg);
            background: var(--mc-bg-sel);
        }
        &_active {
            color: var(--mc-acid);
        }
    }
}
</style>
